/**
 * File versioning types for Papan
 *
 * Content-addressed: versions point to content hashes, not paths.
 * Every file modification creates a new version.
 */

/**
 * Who created a version — the human-or-system at the TOP of the
 * accountability chain.
 *
 * Companion to `WriteOrigin` (the mechanism, in @papan/storage/sync):
 *
 *   actor       = WHO
 *   WriteOrigin = VIA WHAT MECHANISM
 *
 * These are orthogonal — both required. Example: when Bob authorizes
 * Sarah's `app_distribution` app to write on his behalf, actor=Bob
 * (a `user`-typed actor) AND WriteOrigin={kind:'app-oauth', appId:...}.
 *
 * **No `'ai'` value here** — AI agent runs always have a prompting user
 * at the top of the chain. The user is the actor; `WriteOrigin.kind = 'ai'`
 * carries the AI session metadata separately.
 *
 * `share` covers writes that came in through a share-link recipient's
 * connection — the `id` is a synthetic `share:<shareLinkId>[:<email>]`
 * rather than a `users.id`, and the actor isn't a real Papan user.
 * Kept distinct from `user` so version-history readers don't accidentally
 * treat synthetic identities as real accounts (no FK lookups, no profile
 * fetches, no membership joins).
 */
export interface VersionActor {
  type: 'user' | 'system' | 'share' | 'app-actor'
  id: string
  name: string
  email?: string
  /**
   * The acting tenant (membership tenant for `user`, the tenant the write
   * landed in for `share`/`system`). Recorded for audit even when the UI
   * never shows it. Optional: rows written before this field stay `undefined`.
   */
  tenantId?: string
  /**
   * `app-actor` only: the app (its folder id) whose PAIRWISE id `id` is — the id is keyed
   * to one app and means nothing without it. Lets the share view recompute a visitor's
   * own id for that app (its Bin shows them their own script deletions, #775).
   */
  appFolderId?: string
  /**
   * `app-actor` only, a write made from a SHARE SESSION: the link it came through, and the
   * opaque tag of the visit (preview-session `shareVisitTag` — never an identity). The share
   * view's Bin shows a visitor what an app's script deleted for them by these (#775).
   */
  shareLinkId?: string
  visit?: string
  /**
   * `share` only (#776): the PERSON behind a share-link visitor, recorded when the link's
   * audience says so — a PRIVATE link (a native holder: as for any native edit) or a
   * RESTRICTED one after the visitor accepted the disclosure. `visitorUserId` is the
   * account (absent for a no-account email-code visitor); `visitorTenantId` the org they
   * entered as (their stored acting tenant). `tenantId` keeps its meaning — the tenant
   * the write landed in — and `id` its Bin-scoping role.
   */
  visitorUserId?: string
  visitorTenantId?: string
}

/**
 * Canonical builder for a {@link VersionActor}. THE only way to construct one —
 * inline `{ type: 'user', … }` literals are banned (see the lint guard) so that
 * attribution can't drift per call site (login-vs-membership email, missing
 * email, absent tenantId, `id:'unknown'`).
 *
 * Pick the branch by asking **"what kind of id do I have?"** — that's exactly
 * what `type` encodes (the id's namespace / how a reader resolves it):
 *
 *   - `user`   → a real `users.id`            (resolves against the users table)
 *   - `share`  → a share-link recipient       (synthetic `share:<linkId>[:<email>]`)
 *   - `system` → an autonomous mechanism      (a stable constant: `'app-hook'`, …)
 *   - `app-actor` → a person seen THROUGH an app (#775 item 5): the app's per-app
 *                 PAIRWISE id (`deriveActorId`) — never a `users.id`, never resolved —
 *                 with only the identity fields the person consented to give that app.
 *                 What a `scripts.run` write records: no more than `whoami()` disclosed.
 *
 * "Which app" is NOT an actor type — it rides `WriteOrigin.appFolderId`. An
 * app-driven write is `user` (a human invoker) or `system` (a hook), never `'app'`.
 *
 * `email` MUST be the membership `(tenant, email)` identity (never the login
 * email) for `user`; the verified email for `share`.
 */
export type WriteActorInput =
  | { kind: 'user'; userId: string; email: string | null | undefined; tenantId: string | null | undefined; name?: string | null }
  | { kind: 'share'; shareLinkId: string; email?: string | null; tenantId: string | null | undefined; name?: string | null
      /** #776: the id's per-visitor suffix when it must NOT be the recorded email (a public
       *  link's opaque visitor key; a restricted link's verified email when the recorded
       *  identity email differs). Omitted ⇒ `email`, as before. `null` ⇒ the bare link. */
      idSuffix?: string | null
      visitorUserId?: string | null; visitorTenantId?: string | null }
  | { kind: 'system'; mechanism: string; label: string; tenantId?: string | null | undefined }
  | { kind: 'app-actor'; actorId: string; email?: string | null; name?: string | null; tenantId?: string | null; appFolderId?: string | null; shareLinkId?: string | null; visit?: string | null }

/** Canonical synthetic id for a share-link writer: `share:<linkId>[:<email>]`. */
export function shareActorId(shareLinkId: string, email?: string): string {
  const base = shareLinkId.startsWith('share:') ? shareLinkId.slice('share:'.length) : shareLinkId
  // `base` may already carry an `:<email>` suffix (callers passing a pre-formed
  // token id); don't double-append.
  if (email && !base.includes(':')) return `share:${base}:${email}`
  return `share:${base}`
}

/**
 * #1263 — a share-link visitor's actor as it may appear ON THE WIRE (realtime events to the owner org,
 * to every org mounting the folder, app hook trigger docs): the LINK alone — `share:<link>`, "Link
 * visitor" — never the visitor's email, account or org (a `share:` id's suffix can be the verified
 * email). The audit keeps the full actor. The ONE definition (`@papan/files`' publisher and the share
 * view's fallback both use it).
 */
export function shareLinkWireActor(shareActorIdValue: string): { type: 'share'; id: string; name: string } {
  // The link: the id's first segment after an optional `share:` prefix (a bare link id included). A
  // segment that looks like an email is never a link id — it names no link rather than leak it.
  const seg = (shareActorIdValue.startsWith('share:') ? shareActorIdValue.slice('share:'.length) : shareActorIdValue).split(':')[0]
  const link = containsEmailAddress(seg) ? '' : seg
  return { type: 'share', id: `share:${link}`, name: 'Link visitor' }
}

/** #1263 — THE wire form of any event actor: a share-link visitor is the link alone
 *  (`shareLinkWireActor`); every other actor goes out as it is. `@papan/files`' publisher and the
 *  share view's degraded path both use it, so the two can never send different actors. */
export function wireActorFor<A extends { type: string; id: string }>(actor: A): A | { type: 'share'; id: string; name: string } {
  return actor.type === 'share' ? shareLinkWireActor(actor.id) : actor
}

/**
 * Is `userId` a real ACCOUNT's id — one a person's notifications can be written to? Synthetic principal ids all carry a
 * colon (`apikey:<id>`, `share:<link>`, `mcp:<client>` …); a real account id never does (#1320 code review; the
 * realtime layer states the same family). Used by the notifiers; other checks that test only one prefix
 * (`share:` in may-write-workspace / websocket) are their own — I6.
 */
export function isAccountUserId(userId: string | null | undefined): userId is string {
  return typeof userId === 'string' && userId.length > 0 && !userId.includes(':')
}

export function resolveWriteActor(input: WriteActorInput): VersionActor {
  switch (input.kind) {
    case 'user':
      return {
        type: 'user',
        id: input.userId,
        name: input.name?.trim() || input.email || input.userId,
        email: input.email ?? undefined,
        tenantId: input.tenantId ?? undefined,
      }
    case 'share':
      return {
        type: 'share',
        id: shareActorId(input.shareLinkId, (input.idSuffix === undefined ? input.email : input.idSuffix) ?? undefined),
        name: input.name?.trim() || input.email || input.shareLinkId,
        email: input.email ?? undefined,
        tenantId: input.tenantId ?? undefined,
        ...(input.visitorUserId ? { visitorUserId: input.visitorUserId } : {}),
        ...(input.visitorTenantId ? { visitorTenantId: input.visitorTenantId } : {}),
      }
    case 'system':
      return {
        type: 'system',
        id: input.mechanism,
        name: input.label,
        tenantId: input.tenantId ?? undefined,
      }
    case 'app-actor':
      // Only what was given — absent fields stay ABSENT (not undefined keys), so the
      // stored record carries nothing the consent flow didn't disclose.
      return {
        type: 'app-actor',
        id: input.actorId,
        name: input.name?.trim() || input.email || 'App user',
        ...(input.email ? { email: input.email } : {}),
        ...(input.tenantId ? { tenantId: input.tenantId } : {}),
        ...(input.appFolderId ? { appFolderId: input.appFolderId } : {}),
        ...(input.shareLinkId ? { shareLinkId: input.shareLinkId } : {}),
        ...(input.visit ? { visit: input.visit } : {}),
      }
  }
}

/**
 * E7 PR 0b (owner D127, 2026-10-19) — a SQLite database's live file is its truth and its versions are BACKUPS: every
 * backup version (an engine snapshot, the DB editor's save, the drain, "Snapshot now", the bookkeeping job, an adopt's
 * predecessor) is CREDITED to this system actor, never a person. Who wrote the db since the previous backup is kept
 * beside it as forensic information (the version's `coAuthors`, `databaseBackupWriters`), never as its author.
 */
export const DATABASE_BACKUP_ACTOR: VersionActor = resolveWriteActor({ kind: 'system', mechanism: 'database-backup', label: 'Database backup' })

/** The most writers a backup names (oldest dropped first). */
export const DATABASE_BACKUP_MAX_WRITERS = 20

/** Codex rounds — the ONE email shape every scrub uses (`containsEmailAddress`, `scrubEmailAddresses`).
 *  `local@domain.tld`: a local part of any run without spaces, `@` or `<>()[],;:"` (or a quoted one, `"a b"@x.io`);
 *  dot-separated domain labels of letters / digits / marks / hyphens in ANY script; a top-level domain of 2+ LETTERS in
 *  any script or an IDN's punycode `xn--…` label, in any case. A handle like `@mira`, text like `team @ home` or a
 *  version like `Orders@1.2.0` / `@v2.0` / `@1.2.0-beta.1` is not one (its last label is digits); `alice@example.com`,
 *  `Bob <bob@x.io>`, `bob@163.com`, `alice@例子.公司` and `alice@example.XN--P1AI` are.
 *
 *  Found on the ORIGINAL text, never normalised — the `@` is any form NFKC maps to `@` (`＠` U+FF20, `﹫` U+FE6B), the
 *  dot any form NFKC maps to `.` (`․` U+2024, `﹒` U+FE52, `．` U+FF0E) plus the full stops IDNA separates labels with
 *  (`。` U+3002, `｡` U+FF61); `version-authors-for-viewer-i252.test.ts` pins both sets against NFKC — and only the matched spans are replaced: `① ﬁle, ５㎏` stays as written, and a version whose last part is
 *  stylised (`ⓡⓔⓛⓔⓐⓢⓔ` — symbols, not letters) is no email. */
const EMAIL_SHAPE_SOURCE =
  '(?:"[^"\\n]+"|[^\\s@\\uFF20\\uFE6B<>()\\[\\],;:"]+)[@\\uFF20\\uFE6B]' +
  '(?:[\\p{L}\\p{N}\\p{M}](?:[\\p{L}\\p{N}\\p{M}-]*[\\p{L}\\p{N}\\p{M}])?[.\\u2024\\uFE52\\uFF0E\\u3002\\uFF61])+' +
  '(?:xn--[\\p{L}\\p{N}-]+|\\p{L}[\\p{L}\\p{M}]+)(?![\\p{L}\\p{N}\\p{M}-])'
const EMAIL_IN_TEXT = new RegExp(EMAIL_SHAPE_SOURCE, 'iu')
export const containsEmailAddress = (s: string | null | undefined): boolean => !!s && EMAIL_IN_TEXT.test(s)
/** `s` with every email address in it replaced by `replacement`; everything else exactly as written. */
export const scrubEmailAddresses = (s: string, replacement: string): string => s.replace(new RegExp(EMAIL_SHAPE_SOURCE, 'giu'), replacement)
/** A system actor's label as a writer: a hook's label is `<app name>@<version>` — judged WHOLE (never by stripping a
 *  suffix: `bob` + `163.com` is `bob@163.com`); a version is no email (its last part is numeric), an email anywhere in it
 *  ⇒ "App". */
function systemLabelShape(label: string): string {
  return containsEmailAddress(label) ? 'App' : label
}

/**
 * Codex round (D127 / D129) — a backup's writer as it is STORED and shown: DISPLAY-ONLY. A db's version history is read
 * by anyone who can read the db — another org through a `.shared`, a share-link visitor, a PDApp viewer — so a writer
 * never carries an email, a tenant id or a share / visit identity, and its name is never an email:
 *  - a person: their opaque user id and display name (an email-like name ⇒ "A collaborator");
 *  - a share-link visitor: "Link visitor", keyed by the LINK only (`share:<link>` — the per-visitor suffix can be an
 *    email);
 *  - an app actor: the app — its pairwise opaque id, the app folder, the consented display name (an email-like one ⇒
 *    "App"); never the viewer's email / org, a link or a visit;
 *  - a system actor: its label — a hook's `<app>@<version>` judged without the version; an email-like one ⇒ "App".
 */
export function databaseBackupWriterShape(w: VersionActor): VersionActor {
  switch (w.type) {
    case 'share':
      return shareLinkWireActor(w.id)
    case 'app-actor':
      return { type: 'app-actor', id: w.id, name: containsEmailAddress(w.name) || !w.name ? 'App' : w.name, ...(w.appFolderId ? { appFolderId: w.appFolderId } : {}) }
    case 'system':
      return { type: 'system', id: containsEmailAddress(w.id) ? 'system' : w.id, name: systemLabelShape(w.name) }
    default:
      return { type: w.type, id: containsEmailAddress(w.id) ? 'person' : w.id, name: containsEmailAddress(w.name) || !w.name ? 'A collaborator' : w.name }
  }
}

/** The writers since the previous backup, in their DISPLAY-ONLY shape (`databaseBackupWriterShape`), deduped by
 *  (type, id), at most `DATABASE_BACKUP_MAX_WRITERS` — the backup actor itself never among them. */
export function databaseBackupWriters(writers: ReadonlyArray<VersionActor | null | undefined>): VersionActor[] {
  const out: VersionActor[] = []
  const seen = new Set<string>()
  for (const raw of writers) {
    if (!raw || (raw.type === DATABASE_BACKUP_ACTOR.type && raw.id === DATABASE_BACKUP_ACTOR.id)) continue
    const w = databaseBackupWriterShape(raw)
    const key = `${w.type}\u0000${w.id}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(w)
  }
  return out.slice(-DATABASE_BACKUP_MAX_WRITERS)
}

/** "includes changes by B, C and 2 others" — a BACKUP's description note, from its display-only writers (never an
 *  email: `databaseBackupWriters` shapes them). */
export function describeDatabaseBackupWriters(writers: readonly VersionActor[]): string {
  const names = databaseBackupWriters(writers).map((a) => a.name)
  if (names.length === 0) return ''
  if (names.length === 1) return `includes changes by ${names[0]}`
  if (names.length === 2) return `includes changes by ${names[0]} and ${names[1]}`
  const rest = names.length - 2
  return `includes changes by ${names[0]}, ${names[1]} and ${rest} other${rest === 1 ? '' : 's'}`
}

/**
 * How the version was created
 */
export type VersionSource =
  | 'upload'
  | 'editor'
  | 'ai_tool'
  | 'restore'
  | 'copy'
  | 'quarantine-restore'
  | 'system'
  // Disk-orphan baseline reconciliation by the file-sync pipeline:
  // a disk file the VFS didn't know about gets imported with this source
  // so it stays out of agent revert history (which only attributes
  // 'ai_tool'). See docs/plans/2026-05-18_(completed)_VFS_SYNC_EXTRACTION.md.
  | 'reconcile'

/**
 * A single version of a file
 */
export interface Version {
  id: string                    // e.g., "v_1706700000000"
  fileId: string                // File ID this version belongs to
  versionNumber?: number        // Monotonic per-file (v1, v2, ...)
  contentHash: string           // SHA-256 hex
  size: number
  createdBy: VersionActor | null
  source: VersionSource | string
  description?: string
  createdAt: Date
}

/**
 * Item in bin (trash)
 */
export interface BinItem {
  id: string                    // Bin ID (timestamp-based)
  originalParentId: string | null
  originalName: string
  isFolder: boolean
  totalSize: number
  deletedAt: Date
  deletedBy: VersionActor | null
  expiresAt: Date
}

/**
 * Quarantine item
 */
export interface QuarantineItem {
  id: string
  originalParentId: string | null
  originalName: string
  contentHash: string
  threat: string
  scanner: string
  size: number
  detectedAt: Date
  detectedBy: VersionActor | null
}

// ─── I252 / D133 — version authors per VIEWER ────────────────────────────────

/**
 * Who reads a file's version history:
 *  - `own-org` — a member of the file's own org, resolving natively: authors as stored (as today);
 *  - `other-org` — anyone else signed in (a cross-org reader through a `.shared` / pointer route): a DISPLAY NAME only;
 *  - `link` — a share-link session (a public or identified Edit link, a PDApp's app link): NOTHING about who (the share
 *    view's "never who" rule — an Edit link may be public; its holder learns the file's history, not its editors).
 */
export type VersionViewer = 'own-org' | 'other-org' | 'link'

/** The shape an OUTSIDER sees of a version author: its kind and a display name — never an email, a tenant id, a user
 *  id, a share-link / visit identity or an app's actor id (`id` is a constant). A name that is an email ⇒ a label. */
export function versionAuthorDisplay(a: VersionActor): VersionActor {
  const label = a.type === 'share' ? 'Link visitor'
    : a.type === 'app-actor' ? (containsEmailAddress(a.name) || !a.name ? 'App' : a.name)
      : a.type === 'system' ? (containsEmailAddress(a.name) ? 'App' : a.name)
        : (containsEmailAddress(a.name) || !a.name ? 'A collaborator' : a.name)
  return { type: a.type, id: 'hidden', name: label }
}

/** A stored description as an outsider reads it: every email replaced (older DB-editor saves wrote "includes changes
 *  by <name or email>"); for a `link` viewer the "— includes changes by …" note (other people's names) goes whole. */
export function versionDescriptionForViewer(description: string | null | undefined, viewer: VersionViewer): string | undefined {
  if (description == null) return undefined
  if (viewer === 'own-org') return description
  let d = description
  if (viewer === 'link') d = d.replace(/\s*—\s*includes changes by .*$/s, '')
  return scrubEmailAddresses(d, 'a collaborator')
}

/**
 * THE one serializer of a version's authors for a viewer (I252, D133) — every route that hands out stored version
 * authors or descriptions passes through it (`version-authors-one-serializer` guard test): `own-org` unchanged;
 * `other-org` display-only `createdBy` / `coAuthors`; `link` neither. Descriptions scrubbed for outsiders.
 */
export function versionAuthorsForViewer<T extends { createdBy?: VersionActor | null; coAuthors?: VersionActor[] | null; description?: string | null }>(v: T, viewer: VersionViewer): T {
  if (viewer === 'own-org') return v
  // review: an id that names an app or an actor never leaves to an outsider either (`appFolderId` — the writing app)
  const { createdBy, coAuthors, description, appFolderId: _app, ...rest } = v as T & { appFolderId?: unknown }
  const desc = versionDescriptionForViewer(description, viewer)
  if (viewer === 'link') return { ...rest, ...(desc !== undefined ? { description: desc } : {}) } as T
  return {
    ...rest,
    createdBy: createdBy ? versionAuthorDisplay(createdBy) : (createdBy ?? null),
    ...(coAuthors?.length ? { coAuthors: databaseBackupWritersDisplay(coAuthors) } : {}),
    ...(desc !== undefined ? { description: desc } : {}),
  } as T
}
function databaseBackupWritersDisplay(list: readonly VersionActor[]): VersionActor[] {
  return list.map(versionAuthorDisplay)
}
